/**
 * DocumentImport.js  (#111, improvements #396)
 *
 * Purpose:
 * - A new page providing a staged workflow to go from an uploaded source document
 *   to committed requirements and, optionally, an AI-led system design.
 *
 * Stages (MUI Stepper):
 *   0 Upload     — pick a PDF/DOCX/MD/TXT; backend extracts plain text.
 *   1 Extract    — AI extracts candidate requirements, OR (for an app-exported .md)
 *                  the document structure is preserved verbatim with no AI rewrite (#396 P8).
 *   2 Review     — edit / include / discard candidates (or preview the preserved tree).
 *   3 Commit     — choose a project + a new container OR an existing parent, then create.
 *   4 Design     — optional AI-led system-design hierarchy from the requirements.
 *   5 Done       — summary.
 *
 * Nothing is persisted until the explicit "Commit" actions in stages 3 and 4.
 */

import React, { useState, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
  Box, Stepper, Step, StepLabel, Button, Typography, Paper, Stack,
  CircularProgress, Alert, TextField, Checkbox, IconButton, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, FormControl, InputLabel,
  Select, MenuItem, Radio, RadioGroup, FormControlLabel, Chip, Switch,
  LinearProgress, useMediaQuery
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlineOutlined';
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded';
import AccountTreeRoundedIcon from '@mui/icons-material/AccountTreeRounded';
import { documentApi, requirementsApi } from '../services/api';
import { iamApi } from '../services/iamApi';
import { getActiveOrg } from '../services/authToken';
import UnifiedContainerSelector from '../components/tree/UnifiedContainerSelector';
import NewProjectDialog from '../components/dialogs/NewProjectDialog';

const STEPS = ['Upload', 'Extract', 'Review', 'Commit', 'System design', 'Done'];

// Count the nodes in a preserved-structure tree (for the commit label / summary).
function countNodes(node) {
  if (!node) return 0;
  return 1 + (node.children || []).reduce((acc, c) => acc + countNodes(c), 0);
}

// Recursive renderer for the proposed (staged) system-design / preserved tree.
function DesignNode({ node, depth = 0 }) {
  if (!node) return null;
  return (
    <Box sx={{ ml: depth * 2, mt: 0.5 }}>
      <Typography variant="body2" sx={{ fontWeight: depth === 0 ? 700 : 500 }}>
        {node.title}
      </Typography>
      {node.description && (
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>{node.description}</Typography>
      )}
      {(node.children || []).map((c) => (
        <DesignNode key={c.title || c.description} node={c} depth={depth + 1} />
      ))}
    </Box>
  );
}

const isMarkdownName = (name) => /\.(md|markdown)$/i.test(name || '');

export default function DocumentImport() {
  const isMobile = useMediaQuery('(max-width:640px)');
  const [activeStep, setActiveStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // Stage state
  const [file, setFile] = useState(null);
  const [parsed, setParsed] = useState(null); // {doc_name, text, char_count, word_count, truncated}
  const [candidates, setCandidates] = useState([]); // [{include, title, description, type, ...}]
  const [extractTruncated, setExtractTruncated] = useState(false); // #282

  // #396 P8: structure-preserving import (no AI rewrite). When set, the flat
  // candidate table is replaced by a nested-tree preview and commit goes through
  // commit_system_design instead of the flat commit.
  const [preserveStructure, setPreserveStructure] = useState(false);
  const [structuredTree, setStructuredTree] = useState(null);

  // #396 P5: `mode` drives the RadioGroup ('new'|'existing'); `parentId` holds the
  // picked target item — split so selecting an item can never clear the radio.
  const [mode, setMode] = useState('new'); // 'new' | 'existing'
  const [parentId, setParentId] = useState(null);
  const [parentTitle, setParentTitle] = useState('');
  const [containerTitle, setContainerTitle] = useState('');

  // #396 P2/P3: a NEW container/tree needs an explicit project (nothing to inherit
  // one from). Choose an existing project or create one.
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [newProjectOpen, setNewProjectOpen] = useState(false);

  const [committed, setCommitted] = useState(null); // {parent_id, created:[...]}
  const [design, setDesign] = useState(null); // staged system tree
  const [designResult, setDesignResult] = useState(null); // {root_id, created_count}

  const run = useCallback(async (fn) => {
    setBusy(true);
    setError('');
    try {
      await fn();
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || 'Something went wrong';
      setError(typeof detail === 'string' ? detail : JSON.stringify(detail));
    } finally {
      setBusy(false);
    }
  }, []);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    setFile(f || null);
    setParsed(null);
    setError('');
    // Default the structure-preserving toggle ON for Markdown uploads (the
    // app-exported round-trip case #396 P8); OFF for PDF/DOCX/TXT.
    setPreserveStructure(isMarkdownName(f?.name));
  };

  const handleParse = () => run(async () => {
    const data = await documentApi.parse(file);
    setParsed(data);
    if (!containerTitle) setContainerTitle(`Imported: ${data.doc_name}`);
  });

  const handleExtract = () => run(async () => {
    if (preserveStructure) {
      // #396 P8: rebuild the nested tree verbatim from the Markdown, no AI rewrite.
      const tree = await documentApi.parseStructured(parsed.text, parsed.doc_name);
      setStructuredTree(tree);
      setCandidates([]);
      setExtractTruncated(false);
      setActiveStep(2);
      return;
    }
    const { requirements, truncated } = await documentApi.extract(parsed.text, parsed.doc_name);
    // #943: `_key` is a React `key=` identity only, stamped on here rather than
    // read from the AI response (which carries no id) — removeCandidate(idx)
    // splices this array, and each row holds its own title/description
    // <TextField>s, so an index key would hand a deleted row's in-progress edit
    // to the next row instead of removing it.
    setCandidates(requirements.map((r) => ({ ...r, include: true, _key: uuidv4() })));
    setStructuredTree(null);
    setExtractTruncated(truncated);
    setActiveStep(2);
  });

  const selected = candidates.filter((c) => c.include);
  const commitCount = structuredTree ? countNodes(structuredTree) : selected.length;

  // Lazy-load the project list the first time the Commit step needs it.
  const loadProjects = useCallback(() => {
    requirementsApi.getProjects()
      .then((rows) => setProjects(Array.isArray(rows) ? rows : []))
      .catch(() => setProjects([]));
  }, []);

  const goToCommit = () => { loadProjects(); setActiveStep(3); };

  const handleCreateProject = (name) => {
    const org = getActiveOrg();
    if (!org) throw new Error('No active organisation — select one first.');
    return iamApi.createProject(org, name);
  };
  const onProjectCreated = (proj) => {
    setNewProjectOpen(false);
    const id = proj?.id || proj?.project_id;
    const title = proj?.title || proj?.name || 'New project';
    if (id) {
      setProjects((p) => [{ id, title }, ...p.filter((x) => x.id !== id)]);
      setProjectId(id);
    }
  };

  const handleCommit = () => run(async () => {
    // Resolve the target parent + project. For an existing item the project is
    // inherited from the parent, so only the new path needs an explicit project.
    if (structuredTree) {
      // #396 P8: commit the preserved tree via commit_system_design. For a NEW project
      // the root heading becomes a new TOP-LEVEL item stamped with that project (a
      // project is a scoping entity, not a tree node) — so pass a null parent and the
      // project as X-Project-Id. For an existing item, nest under it (project inherited).
      const targetParent = mode === 'new' ? null : parentId;
      const result = await documentApi.commitDesign(
        targetParent, structuredTree, mode === 'new' ? projectId : undefined);
      setCommitted({ parent_id: result.root_id, created: new Array(result.created_count || 0).fill({}) });
      setDesignResult(null);
      setActiveStep(5); // structure-preserving import skips the AI design step
      return;
    }
    const payload = {
      // #943: `_key` is a React `key=` identity stamped on at setCandidates(...)
      // time (see there) — strip it alongside `include`, neither belongs in the
      // API payload.
      requirements: selected.map(({ include: _include, _key, ...r }) => r),
      ...(mode === 'new'
        ? { newContainerTitle: containerTitle || `Imported: ${parsed?.doc_name || 'document'}`, projectId }
        : { parentId }),
    };
    const result = await documentApi.commit(payload);
    setCommitted(result);
    setActiveStep(4);
  });

  const handleDesign = () => run(async () => {
    const system = await documentApi.design(selected.map(({ include: _include, _key, ...r }) => r));
    setDesign(system);
  });

  const handleCommitDesign = () => run(async () => {
    const result = await documentApi.commitDesign(committed.parent_id, design);
    setDesignResult(result);
    setActiveStep(5);
  });

  const updateCandidate = (idx, field, value) => {
    setCandidates((prev) => prev.map((c, i) => (i === idx ? { ...c, [field]: value } : c)));
  };
  const removeCandidate = (idx) => {
    setCandidates((prev) => prev.filter((_, i) => i !== idx));
  };

  const resetAll = () => {
    setActiveStep(0); setFile(null); setParsed(null); setCandidates([]);
    setCommitted(null); setDesign(null); setDesignResult(null);
    setMode('new'); setParentId(null); setParentTitle(''); setProjectId('');
    setStructuredTree(null); setPreserveStructure(false);
  };

  // Commit is ready when the target is fully specified.
  const commitReady = mode === 'new' ? !!projectId : !!parentId;

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h4" gutterBottom>Document Import</Typography>
      <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
        Upload a document (PDF, DOCX, Markdown, TXT) and let the AI extract requirements,
        then review, commit, and optionally generate a system design.
      </Typography>

      {isMobile ? (
        // A 6-step horizontal stepper can't fit a phone width without clipping
        // (the "System design" step ran off-screen). Compact progress instead.
        <Box sx={{ mb: 3 }}>
          <Typography variant="overline" sx={{ color: 'text.secondary' }}>
            Step {activeStep + 1} of {STEPS.length}
          </Typography>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>{STEPS[activeStep]}</Typography>
          <LinearProgress
            variant="determinate"
            value={(activeStep / (STEPS.length - 1)) * 100}
            sx={{ height: 6, borderRadius: 3 }}
          />
        </Box>
      ) : (
        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          {STEPS.map((label) => (
            <Step key={label}><StepLabel>{label}</StepLabel></Step>
          ))}
        </Stepper>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper variant="outlined" sx={{ p: 3 }}>
        {/* Stage 0: Upload */}
        {activeStep === 0 && (
          <Stack spacing={2}>
            <Button
              component="label"
              variant="outlined"
              startIcon={<UploadFileRoundedIcon />}
              sx={{ alignSelf: 'flex-start' }}
            >
              Choose file
              <input
                hidden
                type="file"
                accept=".pdf,.docx,.md,.markdown,.txt"
                onChange={onFile}
                data-testid="file-input"
              />
            </Button>
            {file && <Typography variant="body2">Selected: <b>{file.name}</b></Typography>}
            {parsed && (
              <Alert severity="success">
                Parsed {parsed.word_count} words ({parsed.char_count} chars)
                {parsed.truncated && ' — truncated to the size limit'}.
                <Box component="pre" sx={{ mt: 1, maxHeight: 160, overflow: 'auto', fontSize: 12, whiteSpace: 'pre-wrap' }}>
                  {parsed.text.slice(0, 600)}{parsed.text.length > 600 ? '…' : ''}
                </Box>
              </Alert>
            )}
            <Stack direction="row" spacing={1}>
              <Button variant="contained" disabled={!file || busy} onClick={handleParse}>
                {busy ? <CircularProgress size={20} /> : 'Parse'}
              </Button>
              <Button variant="contained" disabled={!parsed || busy} onClick={() => setActiveStep(1)}>
                Next
              </Button>
            </Stack>
          </Stack>
        )}

        {/* Stage 1: Extract */}
        {activeStep === 1 && (
          <Stack spacing={2}>
            <Typography>Process <b>{parsed?.doc_name}</b>.</Typography>
            {/* #396 P8: toggle the structure-preserving path (default on for .md). */}
            <FormControlLabel
              control={(
                <Switch
                  checked={preserveStructure}
                  disabled={busy}
                  onChange={(e) => setPreserveStructure(e.target.checked)}
                  slotProps={{ input: { 'data-testid': 'preserve-structure-toggle' } }}
                />
              )}
              label="Preserve document structure (no AI rewrite)"
            />
            <Typography variant="caption" sx={{ mt: -1, color: 'text.secondary' }}>
              {preserveStructure
                ? 'Headings become the requirement hierarchy verbatim — best for a document exported from this app.'
                : 'The AI reads the text and proposes atomic, EARS-style requirements.'}
            </Typography>
            {/* #396 P1: a labelled progress bar with stage text instead of a bare
                button spinner. A true percentage needs backend streaming of the
                per-chunk extraction (azure_ai.py EXTRACT_CHUNK_CHARS) — TODO
                follow-up: stream chunk progress over SSE and switch this to a
                determinate bar. For now an honest indeterminate bar + stage label. */}
            {busy && (
              <Box data-testid="extract-progress">
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  {preserveStructure ? 'Parsing document structure…' : 'Analysing document…'}
                </Typography>
                <LinearProgress />
              </Box>
            )}
            <Stack direction="row" spacing={1}>
              <Button variant="outlined" disabled={busy} onClick={() => setActiveStep(0)}>Back</Button>
              <Button variant="contained" disabled={busy} onClick={handleExtract}>
                {preserveStructure ? 'Import structure' : 'Extract requirements'}
              </Button>
            </Stack>
          </Stack>
        )}

        {/* Stage 2: Review */}
        {activeStep === 2 && (
          <Stack spacing={2}>
            {structuredTree ? (
              // #396 P8: preserved-structure preview (read-only nested tree).
              <>
                <Typography>
                  Preserved {commitCount} item{commitCount === 1 ? '' : 's'} from the document&apos;s
                  own heading structure — no AI rewrite. Review, then commit.
                </Typography>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: 'action.hover', maxHeight: 420, overflow: 'auto' }}>
                  <DesignNode node={structuredTree} />
                </Paper>
                <Stack direction="row" spacing={1}>
                  <Button variant="outlined" onClick={() => setActiveStep(1)}>Back</Button>
                  <Button variant="contained" onClick={goToCommit}>Next</Button>
                </Stack>
              </>
            ) : (
              <>
                {extractTruncated && (
                  <Alert severity="warning">
                    The AI&apos;s response was cut off before finishing one section of the document —
                    the list below may be missing some requirements. Try splitting the document
                    and importing the remainder separately.
                  </Alert>
                )}
                <Typography>
                  {selected.length} of {candidates.length} selected. Edit or discard before committing.
                </Typography>
                <TableContainer sx={{ maxHeight: 420 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox" />
                        <TableCell>Title</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {candidates.map((c, idx) => (
                        <TableRow key={c._key} hover>
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={!!c.include}
                              onChange={(e) => updateCandidate(idx, 'include', e.target.checked)}
                            />
                          </TableCell>
                          <TableCell sx={{ minWidth: 220 }}>
                            <TextField
                              variant="standard" fullWidth value={c.title}
                              onChange={(e) => updateCandidate(idx, 'title', e.target.value)}
                            />
                          </TableCell>
                          <TableCell sx={{ minWidth: 320 }}>
                            <TextField
                              variant="standard" fullWidth multiline value={c.description || ''}
                              onChange={(e) => updateCandidate(idx, 'description', e.target.value)}
                            />
                          </TableCell>
                          <TableCell>{c.type && <Chip size="small" label={c.type} />}</TableCell>
                          <TableCell>
                            <IconButton size="small" onClick={() => removeCandidate(idx)} aria-label="delete">
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Stack direction="row" spacing={1}>
                  <Button variant="outlined" onClick={() => setActiveStep(1)}>Back</Button>
                  <Button variant="contained" disabled={selected.length === 0} onClick={goToCommit}>
                    Next
                  </Button>
                </Stack>
              </>
            )}
          </Stack>
        )}

        {/* Stage 3: Commit */}
        {activeStep === 3 && (
          <Stack spacing={2}>
            <Typography>Where should the {commitCount} item{commitCount === 1 ? '' : 's'} go?</Typography>
            {/* #396 P5: `mode` drives the radios; the target picker writes `parentId`
                separately, so selecting an item can never clear the selected radio. */}
            <RadioGroup value={mode} onChange={(e) => setMode(e.target.value)}>
              <FormControlLabel value="new" control={<Radio />} label="Create in a new/other project" />
              {mode === 'new' && (
                <Stack spacing={1.5} sx={{ ml: 4, maxWidth: 480 }}>
                  {/* #396 P2/P3: an explicit project is required for the new path. */}
                  <FormControl size="small" fullWidth>
                    <InputLabel>Project</InputLabel>
                    <Select
                      label="Project"
                      value={projects.some((p) => p.id === projectId) ? projectId : ''}
                      onChange={(e) => setProjectId(e.target.value)}
                      data-testid="project-select"
                    >
                      {projects.map((p) => (
                        <MenuItem key={p.id} value={p.id}>{p.title}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Button
                    size="small" sx={{ alignSelf: 'flex-start' }}
                    onClick={() => setNewProjectOpen(true)}
                    data-testid="create-project-btn"
                  >
                    Create a new project
                  </Button>
                  {!structuredTree && (
                    <TextField
                      size="small" label="Container title"
                      value={containerTitle} onChange={(e) => setContainerTitle(e.target.value)}
                    />
                  )}
                  {structuredTree && (
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      The document&apos;s top heading becomes the container.
                    </Typography>
                  )}
                </Stack>
              )}
              <FormControlLabel value="existing" control={<Radio />} label="Add under an existing item" />
            </RadioGroup>
            {mode === 'existing' && (
              // #396 P4: replace the flat Select with the reusable tree picker.
              <Stack direction="row" spacing={2} sx={{ ml: 4, alignItems: 'center' }}>
                <Button
                  variant="outlined" startIcon={<AccountTreeRoundedIcon />}
                  onClick={() => setPickerOpen(true)}
                  data-testid="choose-target-btn"
                >
                  {parentId ? 'Change target…' : 'Choose target…'}
                </Button>
                {parentId && (
                  <Typography variant="body2" data-testid="chosen-target">
                    Target: <b>{parentTitle || parentId}</b>
                  </Typography>
                )}
              </Stack>
            )}
            {busy && (
              // #396 P6: labelled indeterminate bar instead of a bare button spinner.
              <Box data-testid="commit-progress">
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  Creating {commitCount} requirement{commitCount === 1 ? '' : 's'}…
                </Typography>
                <LinearProgress />
              </Box>
            )}
            <Stack direction="row" spacing={1}>
              <Button variant="outlined" disabled={busy} onClick={() => setActiveStep(2)}>Back</Button>
              <Button
                variant="contained" disabled={busy || !commitReady}
                onClick={handleCommit}
                data-testid="commit-btn"
              >
                Commit {commitCount} requirement{commitCount === 1 ? '' : 's'}
              </Button>
            </Stack>
          </Stack>
        )}

        {/* Stage 4: System design */}
        {activeStep === 4 && (
          <Stack spacing={2}>
            <Alert severity="success">
              Created {committed?.created?.length || 0} requirements.
            </Alert>
            <Typography>
              Optionally generate an AI-led system design from these requirements and add it
              under the same parent.
            </Typography>
            {design && (
              <Paper variant="outlined" sx={{ p: 2, bgcolor: 'action.hover' }}>
                <Typography variant="subtitle2" gutterBottom>Proposed system design</Typography>
                <DesignNode node={design} />
              </Paper>
            )}
            {busy && (
              <Box data-testid="design-progress">
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  {design ? 'Creating the system design…' : 'Designing the system…'}
                </Typography>
                <LinearProgress />
              </Box>
            )}
            <Stack direction="row" spacing={1}>
              <Button variant="outlined" disabled={busy} onClick={handleDesign} data-testid="generate-design-btn">
                {design ? 'Re-generate' : 'Generate design'}
              </Button>
              <Button variant="contained" disabled={!design || busy} onClick={handleCommitDesign} data-testid="commit-design-btn">
                Commit design
              </Button>
              <Button variant="text" disabled={busy} onClick={() => setActiveStep(5)} data-testid="skip-design-btn">Skip</Button>
            </Stack>
          </Stack>
        )}

        {/* Stage 5: Done */}
        {activeStep === 5 && (
          <Stack spacing={2}>
            <Alert severity="success">
              Done. {committed?.created?.length || 0} requirements committed
              {designResult ? `, plus a ${designResult.created_count}-node system design` : ''}.
            </Alert>
            <Button
              variant="contained" sx={{ alignSelf: 'flex-start' }}
              onClick={resetAll}
            >
              Import another document
            </Button>
          </Stack>
        )}
      </Paper>

      {/* #396 P4: reusable target picker; scoped to the chosen project when one is set. */}
      <UnifiedContainerSelector
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        selectMode="all"
        showCreateOption
        title="Choose target item"
        projectId={projectId || null}
        onSelect={(id, title) => {
          setParentId(id);
          setParentTitle(title);
          setPickerOpen(false);
        }}
      />

      {/* #396 P3: create a new project inline. */}
      <NewProjectDialog
        open={newProjectOpen}
        title="New project"
        submitLabel="Create"
        onClose={() => setNewProjectOpen(false)}
        onSubmit={handleCreateProject}
        onDone={onProjectCreated}
      />
    </Box>
  );
}
